import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import VerticalStepper from 'Components/UI/VerticalStepper';
import { useFormStepCollectionContext } from 'Components/UI/Accounts/AccountApplication/AccountApplicationContext';
import { convertStepCollectionToSchema } from 'Helpers/FormStep';
import AccountApplicationStepSelector from 'Components/UI/Accounts/AccountApplication/Step';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useHistory } from 'react-router';
import LastSaveTimestamp from 'Components/Modals/AccountApplication/LastSaveTimestamp';
import StepPicklist from 'Components/Modals/AccountApplication/StepPicklist';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import classNames from 'classnames';

const styles = theme => ({
  root: {
    width: '100%',
    display: 'grid',
    gridTemplate: `
    "menu content"
    `,
    gridTemplateRows: '1fr auto',
    placeItems: 'stretch',
    height: '100%',
  },
  menuContainer: {
    padding: theme.spacing(4, 0, 3, 0),
    minHeight: theme.dimensions.md,
    gridArea: 'menu',
    maxHeight: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.facet.backgroundGray,
    alignItems: 'center',
  },
  container: {
    overflow: 'auto',
    gridArea: 'content',
    maxHeight: '100%',
  },
  stepper: {
    height: '100%',
  },
  header: {
    fontWeight: theme.typography.fontWeightFacetMedium,
    textAlign: 'center',
  },
  stepSelect: {
    width: '100%',
    padding: theme.spacing(2),
  },
  round: {
    borderRadius: theme.radius.md,
  },
});

function AccountApplicationContent({ classes }) {
  const {
    state: {
      steps, activeStep, editMode, disabled,
    },
  } = useFormStepCollectionContext();
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({ nodeName: 'Account Application' });
  const containerRef = React.useRef(null);
  const history = useHistory();

  const isMobile = useMobileLayout();


  const handleStepChange = (nextStep) => {
    const nextStepName = steps.resources().index(nextStep).attr('stepName');
    logEvent(`Navigating to ${nextStepName}`);
    const search = new URLSearchParams(history.location.search);
    search.set('activeStep', nextStep);
    history.push({
      pathname: history.location.pathname,
      search: search.toString(),
    });
  };

  const backTop = () => {
    if (containerRef?.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  React.useEffect(() => {
    backTop();
  }, [activeStep]);

  const headerText = (editMode ? null : 'Open Your Account');

  return (
    <div className={classes.root} {...rootNodeProps}>
      <div className={classNames(classes.menuContainer, {
        [classes.round]: editMode,
      })}
      >
        <div className={classes.menu}>
          <Typography color="primary" variant="h4" component="p" className={classes.header}>
            {!steps ? 'Loading...' : headerText}
          </Typography>
          <div>
            <VerticalStepper
              classes={{
                root: classes.stepper,
              }}
              steps={convertStepCollectionToSchema(steps)}
              activeStep={activeStep}
              disabled={disabled}
              onChange={handleStepChange}
              editMode={editMode}
            />
            <div className={classes.leftPad}>
              <LastSaveTimestamp steps={steps} />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.container} ref={containerRef}>
        {isMobile && <StepPicklist onChange={handleStepChange} />}
        <AccountApplicationStepSelector onFailure={backTop} />
      </div>
    </div>
  );
}

AccountApplicationContent.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AccountApplicationContent);
